// Retention value calculator: pure functions shared by the page and the tests.

export const DEFAULT_INPUTS = Object.freeze({
  newCustomers: 20000,
  currentRate: 25,
  targetRate: 28,
  aov: 85,
  repeatOrders: 1.6,
  margin: 55,
});

export const LIMITS = Object.freeze({
  newCustomers: [0, 100_000_000],
  currentRate: [0, 100],
  targetRate: [0, 100],
  aov: [0, 1_000_000],
  repeatOrders: [0, 365],
  margin: [0, 100],
});

/** Turn any user value (string, blank, NaN, negative, huge) into a safe number. */
export function sanitize(key, value) {
  const [min, max] = LIMITS[key];
  const number = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[$,%\s]/g, ''));
  if (!Number.isFinite(number)) return min;
  return Math.min(max, Math.max(min, number));
}

export function sanitizeAll(raw) {
  const clean = {};
  for (const key of Object.keys(DEFAULT_INPUTS)) clean[key] = sanitize(key, raw?.[key]);
  return clean;
}

export function calculate(raw) {
  const input = sanitizeAll(raw);
  const pointsGained = Math.max(0, input.targetRate - input.currentRate);
  const extraCustomers = input.newCustomers * (pointsGained / 100);
  const extraOrders = extraCustomers * input.repeatOrders;
  const extraRevenue = extraOrders * input.aov;
  const extraProfit = extraRevenue * (input.margin / 100);
  const revenuePerPoint = input.newCustomers * 0.01 * input.repeatOrders * input.aov;
  const profitPerPoint = revenuePerPoint * (input.margin / 100);
  return {
    input,
    pointsGained,
    extraCustomers,
    extraOrders,
    extraRevenue,
    extraProfit,
    revenuePerPoint,
    profitPerPoint,
    targetBelowCurrent: input.targetRate < input.currentRate,
  };
}

export function formatMoney(value) {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

export function formatCount(value) {
  return Math.round(value).toLocaleString('en-US');
}

export function formatRate(value) {
  return `${Math.round(value * 10) / 10}%`;
}

/** Read inputs from a URL query string; unknown or invalid values fall back to defaults. */
export function inputsFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const result = { ...DEFAULT_INPUTS };
  for (const key of Object.keys(DEFAULT_INPUTS)) {
    const value = params.get(key);
    if (value !== null && value.trim() !== '' && Number.isFinite(Number(value))) result[key] = sanitize(key, value);
  }
  return result;
}

export function searchFromInputs(inputs) {
  const params = new URLSearchParams();
  const clean = sanitizeAll(inputs);
  for (const key of Object.keys(DEFAULT_INPUTS)) params.set(key, String(clean[key]));
  return `?${params.toString()}`;
}
